import * as React from "react";
import {maxValue, minValue, NumberInput, useRecordContext} from 'react-admin';
import {Edit, Link, required, SaveButton, SelectInput, SimpleForm, TextInput,} from "react-admin";
import {useFormContext, useWatch} from 'react-hook-form';

const VERBOSITIES = [
    {id: "low", name: "Low"},
    {id: "medium", name: "Medium"},
    {id: "high", name: "High"},
];

const EFFORT_LABELS = {
    none: "None",
    low: "Low",
    medium: "Medium",
    high: "High",
    xhigh: "X-High",
};

// Mirrors the ranges the backend enforces, so the admin sees the problem in the
// field instead of a rejected save.
const numberInRange = (min, max) => value => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    const valid = /^\d+(\.\d+)?$/.test(String(value).trim())
        && Number(value) >= min && Number(value) <= max;

    return valid ? undefined : `Must be a number between ${min} and ${max}`;
};

// The catalogue travels with the record so the form can read it, but it is
// reference data rather than a setting, so it must not be posted back.
const stripCatalogue = ({models, ...settings}) => settings;

// Which inputs a model accepts comes from the backend catalogue, so adding a
// model there is enough — nothing here needs to know their names.
const useModelCatalog = () => {
    const record = useRecordContext();
    const model = useWatch({name: 'model'});
    const models = record?.models ?? [];
    const current = models.find(item => item.id === model);

    return {
        model,
        models,
        isKnownModel: Boolean(current),
        efforts: current?.efforts ?? [],
        supports: current?.supports ?? [],
    };
};

// required() only checks that a value is present, so a stored value outside a
// select's choices passes it while the select renders blank, and the save fails
// server-side instead.
const mustBeOneOf = (values, message) => value =>
    values.includes(value) ? undefined : message;

export const AiSettingsEdit = () => {
    const Save = () => {
        return (
            <div className="customerActionToolbar edit-user-btns">
                <Link to={`/ai/settings/custom/show`} className="button bar-purple-button">Cancel</Link>
                <SaveButton alwaysEnable label="Save" className="button bar-gradient-button" icon={<></>}/>
            </div>
        );
    }

    const ModelInput = () => {
        const {model, models, isKnownModel} = useModelCatalog();

        // Kept in the list, disabled, so the admin can see what is stored rather
        // than an empty select with no fields under it.
        const choices = model && !isKnownModel
            ? [...models, {id: model, name: `${model} (no longer supported)`, disabled: true}]
            : models;

        return <SelectInput label="OpenAI Model:" name="model" source="model" fullWidth id="model"
                            validate={[required(), mustBeOneOf(models.map(item => item.id), 'Select a supported model')]}
                            multiline choices={choices}/>;
    };

    const ViewFields = props => {
        const {model, efforts, supports} = useModelCatalog();
        const {setValue} = useFormContext();
        const effort = useWatch({name: 'effort'});

        // Picking a model starts its effort from that model's own default instead
        // of carrying the previous one over. Resetting only an unsupported value
        // was not enough: the GPT-5.x models share one effort list, so switching
        // between them kept whatever was selected before.
        const previousModel = React.useRef(model);
        React.useEffect(() => {
            if (!efforts.length) {
                previousModel.current = model;

                return;
            }

            // undefined until the record arrives, which is not a change of model.
            const switchedModel = previousModel.current !== undefined && previousModel.current !== model;
            previousModel.current = model;

            if (switchedModel || (effort && !efforts.includes(effort))) {
                setValue('effort', efforts.includes('medium') ? 'medium' : efforts[0], {shouldDirty: true});
            }
        }, [model, efforts, effort, setValue]);

        // Reasoning tokens are charged against this budget, so the backend holds
        // reasoning models to a higher floor. Mirrored here for the same reason as
        // the other ranges: the admin sees it in the field, not as a failed save.
        const minTokens = efforts.length ? 8000 : 16;

        // Mirrors the backend: reasoning models reject top_p unless the effort is 'none'.
        const acceptsTopP = supports.includes('top_p') && (!efforts.length || effort === 'none');

        return (
            <div>
                {supports.includes('reasoning_effort') && <SelectInput
                    helperText={
                        <div>
                            <div>- Reducing reasoning effort can result in faster responses and fewer tokens used on reasoning in a response</div>
                            <div>- All models before gpt-5.1 default to medium reasoning effort</div>
                        </div>
                    }
                    label="Effort:" name="effort" source="effort" fullWidth id="effort" multiline defaultValue={'medium'}
                    validate={[required(), mustBeOneOf(efforts, 'Select a supported effort')]}
                    choices={efforts.map(id => ({id, name: EFFORT_LABELS[id] ?? id}))}/>}

                {supports.includes('verbosity') && <SelectInput
                    helperText={
                        <div>
                            - Constrains the verbosity of the model's response. Lower values will result in more concise responses, while higher values will result in more verbose responses.
                        </div>
                    }
                    label="Verbosity:" name="verbosity" source="verbosity" fullWidth id="verbosity" multiline defaultValue={'medium'}
                    validate={[required(), mustBeOneOf(VERBOSITIES.map(item => item.id), 'Select a supported verbosity')]}
                    choices={VERBOSITIES}/>}

                {acceptsTopP && <TextInput helperText={
                    <div>- An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass. So 0.1 means only the tokens comprising the top 10% probability mass are considered.</div>
                }
                    label="Top P (0.0 - 1.0):" defaultValue={1}
                    name="top_p" source="top_p" id="top_p"
                    validate={[required(), numberInRange(0, 1)]} multiline fullWidth/>}

                {supports.includes('temperature') && <TextInput
                    helperText={'Higher values like 0.8 will make the output more random, while lower values like 0.2 will make it more focused and deterministic'}
                    label="Temperature (0.0 - 2.0):"
                    name="temperature" source="temperature" id="temperature"
                    validate={[required(), numberInRange(0, 2)]} multiline fullWidth
                />}

                {supports.includes('max_completion_tokens') && <NumberInput helperText={
                    <div>- An upper bound for the number of tokens that can be generated for a completion, including visible output tokens and reasoning tokens.</div>
                }
                    label="Tokens:" name="token" source="token" id="token"
                    multiline fullWidth
                    defaultValue={25000}
                    validate={[minValue(minTokens), required(), maxValue(32767)]}
                />}
            </div>
        );
    };

    return <div className="edit-customer">
        <h2 className="dailyTitle">AI Model Settings</h2>
        {/* Pessimistic on purpose: the backend refuses an unsupported combination,
            and an optimistic save would redirect to Show with values that then
            flip back once the rejection lands. */}
        <Edit  mutationMode="pessimistic" redirect={'show'} transform={stripCatalogue}>
            <SimpleForm   toolbar={<Save/>}>
                <ModelInput />
                <ViewFields />
                <SelectInput
                    helperText={
                        <div>
                            - Whether or not to store the output of this chat completion request for use in model distillation or evals products.
                        </div>
                    }
                    label="Store:" name="store" source="store" fullWidth id="store"  multiline defaultValue={'false'}
                    choices={[
                        {id: 10, name: "Yes"},
                        {id: 0, name: "No"}
                    ]}/>
                <TextInput rows={10}  label="System Prompt:" name="systemPrompt" source="systemPrompt" id="systemPrompt"  validate={required()} multiline fullWidth/>
                <TextInput rows={10} label="User Prompt Template:" name="promptTemplate" source="promptTemplate" id="promptTemplate"  validate={required()} multiline fullWidth/>
            </SimpleForm>
        </Edit>
    </div>
}
